import React, { useState, useEffect, useCallback } from 'react';
import { HiOutlineStatusOnline, HiOutlineServer, HiOutlineDatabase, HiOutlineClock, HiOutlineCheckCircle, HiOutlineRefresh } from 'react-icons/hi';
import { DashboardSkeleton } from '../components/Skeleton';
import ConfirmModal from '../components/ConfirmModal';

const API_BASE = process.env.REACT_APP_API_URL || '';
const REFRESH_INTERVAL = 10000;

const MONTHS_FR = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];

function formatUptime(ms) {
  if (!ms) return '—';
  const seconds = Math.floor((Date.now() - ms) / 1000);
  const d = Math.floor(seconds / 86400);
  const h = Math.floor((seconds % 86400) / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (d > 0) return `${d}j ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

function getProgressColor(percent) {
  if (percent < 60) return 'green';
  if (percent < 85) return 'yellow';
  return 'red';
}

function formatBackupName(filename) {
  // ds5_2026-03-03_08-02.sql.gz → "3 mars 2026 à 08:02"
  const match = filename.match(/ds5_(\d{4})-(\d{2})-(\d{2})_(\d{2})-(\d{2})/);
  if (!match) return filename;
  const [, y, mo, d, h, mi] = match;
  return `${parseInt(d)} ${MONTHS_FR[parseInt(mo) - 1]} ${y} à ${h}:${mi}`;
}

export default function DashboardPage({ token, role }) {
  const isAdmin = role === 'admin';
  const [data, setData] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [restartingService, setRestartingService] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const [showAllProcesses, setShowAllProcesses] = useState(false);

  const BUSINESS_APPS = ['ds5-backend', 'ds5-frontend', 'ds5-suivi', 'ds5-admin'];

  const fetchData = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/monitoring/overview`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const json = await res.json();
        setData(json);
        setLastUpdate(new Date().toLocaleTimeString());
      }
    } catch (err) {
      console.error('Fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchData]);

  const doRestart = async (serviceName) => {
    setConfirmModal(null);
    setRestartingService(serviceName);
    try {
      const res = await fetch(`${API_BASE}/api/monitoring/pm2/restart/${serviceName}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        setTimeout(fetchData, 2000);
      }
    } catch (err) {
      console.error('Restart error:', err);
    } finally {
      setTimeout(() => setRestartingService(null), 3000);
    }
  };

  const handleRestart = (serviceName) => {
    if (restartingService) return;
    setConfirmModal({
      title: `Redémarrer ${serviceName}`,
      message: `Voulez-vous redémarrer le service "${serviceName}" ? Le service sera momentanément indisponible.`,
      warning: 'Cette action impacte la production.',
      confirmLabel: 'Redémarrer',
      danger: true,
      onConfirm: () => doRestart(serviceName),
    });
  };

  if (loading) {
    return <DashboardSkeleton />;
  }

  if (!data) {
    return <div style={{ textAlign: 'center', padding: 60, color: 'var(--accent-red)' }}>Erreur de chargement</div>;
  }

  return (
    <div className="fade-in">
      {/* System Status Banner */}
      <div className={`status-banner ${data.overall}`}>
        <div className="card-header">
          <div className="card-title">
            <HiOutlineStatusOnline className="icon" /> État du système
          </div>
          <span className={`badge ${data.overall}`}>
            <span className="dot"></span>
            {data.overall === 'healthy' ? 'OPÉRATIONNEL' : data.overall === 'degraded' ? 'DÉGRADÉ' : 'HORS LIGNE'}
          </span>
        </div>
        <h2>
          {data.overall === 'healthy' ? '✓ Tous les systèmes fonctionnels' : data.overall === 'degraded' ? '⚠ Attention requise' : '✕ Systèmes hors ligne'}
        </h2>
        <div className="subtitle">Dernière mise à jour : {lastUpdate}</div>
      </div>

      {/* Service Health Cards */}
      <div className="grid-4" style={{ marginBottom: 24 }}>
        {data.health?.map(svc => (
          <div className="card" key={svc.name}>
            <div className="service-card">
              <span className="service-icon"><HiOutlineServer /></span>
              <div>
                <div className="service-name">{svc.label || svc.name}</div>
                <div className="service-status" style={{ color: svc.status === 'healthy' ? 'var(--accent-green)' : 'var(--accent-red)' }}>
                  <HiOutlineCheckCircle style={{ verticalAlign: 'middle', marginRight: 4 }} />
                  {svc.status === 'healthy' ? 'HEALTHY' : 'DOWN'}
                </div>
                <div className="service-detail">
                  {svc.status === 'healthy'
                    ? <><HiOutlineClock style={{ verticalAlign: 'middle', marginRight: 2 }} /> Temps de réponse : {svc.latencyMs}ms</>
                    : svc.error || 'Unreachable'}
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* MySQL card */}
        <div className="card">
          <div className="service-card">
            <span className="service-icon"><HiOutlineDatabase /></span>
            <div>
              <div className="service-name">MySQL</div>
              <div className="service-status" style={{ color: data.mysql?.connected ? 'var(--accent-green)' : 'var(--accent-red)' }}>
                <HiOutlineCheckCircle style={{ verticalAlign: 'middle', marginRight: 4 }} />
                {data.mysql?.connected ? 'HEALTHY' : 'DOWN'}
              </div>
              <div className="service-detail">
                {data.mysql?.connected ? `${data.mysql.threadsConnected} connections` : 'Disconnected'}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* PM2 Process + Performance */}
      <div className="grid-2-1" style={{ marginBottom: 24 }}>
        {/* PM2 Processes */}
        <div className="card">
          <div className="card-header">
            <div className="card-title"><HiOutlineServer className="icon" /> Processus PM2</div>
            <button
              className="btn btn-outline"
              style={{ padding: '4px 10px', fontSize: 11 }}
              onClick={() => setShowAllProcesses(v => !v)}
            >
              {showAllProcesses ? 'Apps métier uniquement' : 'Afficher tout'}
            </button>
          </div>
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Service</th>
                  <th>Version</th>
                  <th>Status</th>
                  <th>CPU</th>
                  <th>RAM</th>
                  <th>Uptime</th>
                  {isAdmin && <th>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {(data.services || [])
                  .filter(svc => showAllProcesses || BUSINESS_APPS.includes(svc.name))
                  .map(svc => (
                  <tr key={svc.name}>
                    <td style={{ fontWeight: 500 }}>{svc.name}</td>
                    <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{svc.version || '—'}</td>
                    <td><span className={`badge ${svc.status}`}>{svc.status}</span></td>
                    <td>{svc.cpu}%</td>
                    <td>{svc.memory} Mo</td>
                    <td><HiOutlineClock style={{ verticalAlign: 'middle', marginRight: 4 }} />{formatUptime(svc.uptime)}</td>
                    {isAdmin && (
                    <td>
                      <button
                        className="btn-icon"
                        title={`Redémarrer ${svc.name}`}
                        disabled={restartingService === svc.name}
                        onClick={() => handleRestart(svc.name)}
                      >
                        <HiOutlineRefresh className={restartingService === svc.name ? 'spin' : ''} />
                      </button>
                    </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Performance / System */}
        <div className="card">
          <div className="card-header">
            <div className="card-title"><HiOutlineCheckCircle className="icon" /> Performance</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* CPU */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span className="metric-label">CPU</span>
                <span style={{ fontWeight: 600 }}>{data.system?.cpu?.percent ?? Math.round(data.system?.cpu?.load1 * 50)}%</span>
              </div>
              <div className="progress-bar">
                <div
                  className={`progress-fill ${getProgressColor(data.system?.cpu?.percent ?? Math.round(data.system?.cpu?.load1 * 50))}`}
                  style={{ width: `${Math.min(data.system?.cpu?.percent ?? Math.round(data.system?.cpu?.load1 * 50), 100)}%` }}
                />
              </div>
            </div>

            {/* RAM */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span className="metric-label">Mémoire</span>
                <span style={{ fontWeight: 600 }}>{data.system?.memory?.percent}%</span>
              </div>
              <div className="progress-bar">
                <div
                  className={`progress-fill ${getProgressColor(data.system?.memory?.percent)}`}
                  style={{ width: `${data.system?.memory?.percent}%` }}
                />
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                {data.system?.memory?.usedMB} / {data.system?.memory?.totalMB} Mo
              </div>
            </div>

            {/* Disk */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span className="metric-label">Disque</span>
                <span style={{ fontWeight: 600 }}>{data.system?.disk?.percent}%</span>
              </div>
              <div className="progress-bar">
                <div
                  className={`progress-fill ${getProgressColor(data.system?.disk?.percent)}`}
                  style={{ width: `${data.system?.disk?.percent}%` }}
                />
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
                {Math.round(data.system?.disk?.usedMB / 1024)} / {Math.round(data.system?.disk?.totalMB / 1024)} Go
              </div>
            </div>

            {/* Uptime */}
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 8, borderTop: '1px solid var(--border-color)' }}>
              <span className="metric-label">Uptime serveur</span>
              <span style={{ fontWeight: 600, color: 'var(--accent-green)' }}>{data.system?.uptime?.formatted}</span>
            </div>

            {/* MySQL size */}
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span className="metric-label">Taille DB</span>
              <span style={{ fontWeight: 600 }}>{(data.mysql?.dbSizeMB / 1024).toFixed(1)} Go</span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Backups */}
      {data.backups?.length > 0 && (
        <div className="card">
          <div className="card-header">
            <div className="card-title"><HiOutlineDatabase className="icon" /> Sauvegardes récentes</div>
            <span className={`badge healthy`}>{data.backups.length} disponibles</span>
          </div>
          {data.backups.map(b => (
            <div className="backup-row" key={b.filename}>
              <div className="backup-info">
                <div className="backup-filename">{formatBackupName(b.filename)}</div>
                <div className="backup-details">{b.filename} — {b.sizeMB} Mo</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Confirm modal (restart) */}
      <ConfirmModal
        open={!!confirmModal}
        title={confirmModal?.title}
        message={confirmModal?.message}
        warning={confirmModal?.warning}
        confirmLabel={confirmModal?.confirmLabel}
        danger={confirmModal?.danger}
        onConfirm={confirmModal?.onConfirm}
        onCancel={() => setConfirmModal(null)}
      />
    </div>
  );
}
